'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, Trophy, X, AlertTriangle } from 'lucide-react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id, reason = 'manual') => {
    setToasts((prev) => {
      const toast = prev.find((item) => item.id === id);
      if (!toast) return prev;
      if (toast.timerId) {
        clearTimeout(toast.timerId);
      }
      if (toast.onDismiss) {
        queueMicrotask(() => toast.onDismiss({ id, reason }));
      }
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const showToast = useCallback(({
    title,
    description,
    descriptionClassName,
    type = 'info',
    duration = 5000,
    onDismiss,
    actionLabel,
    onAction,
    onClick,

  } = {}) => {
    const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

    const newToast = {
      id,
      title,
      description,
      descriptionClassName,
      type,
      duration,
      isPaused: false,
      remaining: duration,
      startTime: Date.now(),
      onDismiss,
      actionLabel,
      onAction,
      onClick,

    };

    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      newToast.timerId = setTimeout(() => removeToast(id, 'timeout'), duration);
    }
    return id;
  }, [removeToast]);

  useEffect(() => {
    const syncTimersWithVisibility = () => {
      const visible = document.visibilityState === 'visible';
      setToasts((current) => current.map((toast) => {
        if (!visible && toast.timerId) {
          clearTimeout(toast.timerId);
          const elapsed = Date.now() - toast.startTime;
          return {
            ...toast,
            isPaused: true,
            timerId: null,
            remaining: Math.max(0, toast.remaining - elapsed),
          };
        }
        if (visible && toast.isPaused) {
          const timerId = setTimeout(() => removeToast(toast.id, 'timeout'), toast.remaining);
          return {
            ...toast,
            isPaused: false,
            timerId,
            startTime: Date.now(),
          };
        }
        return toast;
      }));
    };
    document.addEventListener('visibilitychange', syncTimersWithVisibility);
    return () => document.removeEventListener('visibilitychange', syncTimersWithVisibility);
  }, [removeToast]);

  // Handle pause/resume on hover
  const handleMouseEnter = useCallback((id) => {
    setToasts((prev) => prev.map(toast => {
      if (toast.id === id && toast.timerId) {
        clearTimeout(toast.timerId);
        const elapsed = Date.now() - toast.startTime;
        return {
          ...toast,
          isPaused: true,
          timerId: null,
          remaining: Math.max(0, toast.remaining - elapsed)
        };
      }
      return toast;
    }));
  }, []);

  const handleMouseLeave = useCallback((id) => {
    setToasts((prev) => prev.map(toast => {
      if (toast.id === id && toast.isPaused && document.visibilityState === 'visible') {
        const newTimerId = setTimeout(() => removeToast(id, 'timeout'), toast.remaining);
        return {
          ...toast,
          isPaused: false,
          startTime: Date.now(),
          timerId: newTimerId
        };
      }
      return toast;
    }));
  }, [removeToast]);

  const value = useMemo(
    () => ({
      showToast,
      removeToast,
    }),
    [showToast, removeToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-6 right-6 z-[9999] flex w-[calc(100%-3rem)] max-w-[430px] flex-col gap-3">
        {toasts.map((toast) => {
          const Icon = {
            success: CheckCircle2,
            error: AlertCircle,
            warning: AlertTriangle,
            info: Info,
            reward: Trophy,
          }[toast.type] || Info;

          const styles = {
            success: 'border-emerald-500/20 bg-emerald-950/90 text-emerald-50 shadow-emerald-900/20',
            error: 'border-rose-500/20 bg-rose-950/90 text-rose-50 shadow-rose-900/20',
            warning: 'border-amber-500/20 bg-amber-950/90 text-amber-50 shadow-amber-900/20',
            info: 'border-blue-500/20 bg-blue-950/90 text-blue-50 shadow-blue-900/20',
            reward: 'border-amber-300/55 bg-[linear-gradient(135deg,rgba(72,42,4,.98),rgba(30,20,7,.98))] text-amber-50 shadow-[0_20px_70px_rgba(245,158,11,.32),0_0_0_1px_rgba(252,211,77,.14)]',
          }[toast.type] || 'border-neutral-800 bg-neutral-900/90 text-neutral-50';

          const iconColor = {
            success: 'text-emerald-400',
            error: 'text-rose-400',
            warning: 'text-amber-400',
            info: 'text-blue-400',
            reward: 'text-amber-300',
          }[toast.type];

          return (
            <div
              key={toast.id}
              onMouseEnter={() => handleMouseEnter(toast.id)}
              onMouseLeave={() => handleMouseLeave(toast.id)}
              role="alert"
              aria-live="assertive"
              className={`
                pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-xl border p-4 shadow-lg backdrop-blur-md transition-all duration-300 animate-in slide-in-from-right-full fade-in
                ${toast.onClick ? 'cursor-pointer' : ''}
                ${styles}
              `}
            >
              {toast.onClick ? (
                <button
                  type="button"
                  onClick={toast.onClick}
                  aria-label={toast.title}
                  className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-300/70"
                />
              ) : null}

              <span className={`${toast.type === 'reward' ? 'grid h-11 w-11 shrink-0 place-items-center rounded-full border border-amber-200/30 bg-amber-300/12 shadow-[inset_0_1px_0_rgba(255,255,255,.15)]' : 'mt-0.5'} pointer-events-none relative z-10`}>
                <Icon className={`${toast.type === 'reward' ? 'h-6 w-6' : 'h-5 w-5'} shrink-0 ${iconColor}`} />
              </span>

              <div className="pointer-events-none relative z-10 flex-1 gap-1">
                {toast.title && <div className={`${toast.type === 'reward' ? 'whitespace-nowrap pr-6 text-sm' : ''} font-semibold leading-tight`}>{toast.title}</div>}
                {toast.description && (
                  <div className={`mt-1 text-sm opacity-90 leading-relaxed ${toast.descriptionClassName || ''}`}>
                    {toast.description}
                  </div>
                )}

                {toast.actionLabel && toast.onAction ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      toast.onAction();
                      removeToast(toast.id, 'action');
                    }}
                    className="pointer-events-auto mt-3 rounded-lg border border-[#F6B51B]/35 bg-[#F6B51B] px-3 py-1.5 text-xs font-semibold text-[#171006] shadow-[0_8px_24px_rgba(246,181,27,.2)] transition hover:scale-[1.02] hover:bg-[#FFD56A]"
                  >
                    {toast.actionLabel}
                  </button>
                ) : null}

              </div>

              <button
                onClick={(event) => {
                  event.stopPropagation();
                  removeToast(toast.id, 'manual');
                }}
                className="absolute right-2 top-2 z-20 rounded-md p-1 opacity-50 transition-opacity hover:bg-white/10 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};
